import { Component, inject, OnInit, signal } from '@angular/core';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { IcpSearchService } from '../../../create-brief-basic/steps/creator-profile/service/icp-search/icp-search.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'agent-step-creator-profile',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './creator-profile.component.html',
  styleUrl: './creator-profile.component.css'
})
export class CreatorProfileComponent implements OnInit {

  reviewContentMode = signal<boolean>(false);
  loadingAiRecommendation = signal<boolean>(false);
  icpRecommendation = signal<{ json: any, paragraph: { sub_heading: string, paragraph: string }[] } | undefined>(undefined);

  // Typewriter chat simulation
  chatMessages = signal<{ text: string; displayedText: string; isComplete: boolean }[]>([]);
  currentMessageIndex = signal<number>(0);
  currentCharIndex = signal<number>(0);
  private typewriterInterval: any;
  private messageDelay: any;

  /* Services */
  private icpSearchService = inject(IcpSearchService);
  public createBriefService = inject(CreateBriefService);
  /* Services - END */

  ngOnInit(): void {
    this.getAiRecommendation();
  }

  getAiRecommendation() {
    this.loadingAiRecommendation.set(true);
    this.startChatSimulation();

    const brandName = this.createBriefService.currentUserService.focussedBrand.companyName;
    const briefTitle = this.createBriefService.basicInfoForm.briefTitle().value();
    const product = this.createBriefService.selectedProduct();
    const goal = this.createBriefService.selectedBriefGoal();

    if (brandName && briefTitle && product && goal) {
      this.icpSearchService.openAiCreatorProfileSearch(brandName, briefTitle, product, goal).then((response) => { 
        console.log('ICP: ', response);
        this.icpRecommendation.set(response);
        this.loadingAiRecommendation.set(false);
        this.useAiRecommendation();
        this.stopChatSimulation();
      })
    } else {
      this.loadingAiRecommendation.set(false);
      this.stopChatSimulation();
      console.error('Brief title, product, and goal are required');
    }
  }

  useAiRecommendation() {
    let search_string = '';

    Object.keys(this.icpRecommendation()?.json).forEach((key) => {
      search_string += this.icpRecommendation()?.json[key] + ' ';
    });
    this.createBriefService.creatorProfileModel.set({
      creator_persona: this.icpRecommendation()?.json.creator_persona,
      creator_audience: this.icpRecommendation()?.json.creator_audience,
      creator_content_style: this.icpRecommendation()?.json.creator_content_style || '',
      creator_engagement_metrics: this.icpRecommendation()?.json.creator_engagement_metrics || '',
      creator_specific_characteristics: this.icpRecommendation()?.json.creator_specific_characteristics || '',
      search_string: search_string,
    });

    this.reviewContentMode.set(true);
  }

  startChatSimulation() {
    // Initialize chat messages
    const messages = [
      "Analyzing your brief requirements...",
      "Identifying key creator characteristics...",
      "Matching audience demographics...",
      "Refining ideal creator profile...",
      "Double checking recommendations...",
      "Almost done! Finalizing recommendations...",
      "Okay, bear with me one moment......."
    ];

    this.chatMessages.set(messages.map(text => ({ text, displayedText: '', isComplete: false })));
    this.currentMessageIndex.set(0);
    this.currentCharIndex.set(0);

    this.typeNextMessage();
  }

  typeNextMessage() {
    const messages = this.chatMessages();
    const currentIndex = this.currentMessageIndex();

    if (currentIndex >= messages.length) {
      return;
    }

    const currentMessage = messages[currentIndex];
    this.currentCharIndex.set(0);

    this.typewriterInterval = setInterval(() => {
      const charIndex = this.currentCharIndex();
      const message = messages[currentIndex];

      if (charIndex < message.text.length) {
        const displayedText = message.text.substring(0, charIndex + 1);
        const updatedMessages = [...messages];
        updatedMessages[currentIndex] = { ...message, displayedText };
        this.chatMessages.set(updatedMessages);
        this.currentCharIndex.set(charIndex + 1);
      } else {
        // Message complete
        const updatedMessages = [...messages];
        updatedMessages[currentIndex] = { ...message, displayedText: message.text, isComplete: true };
        this.chatMessages.set(updatedMessages);
        clearInterval(this.typewriterInterval);

        // Move to next message after a delay
        if (currentIndex < messages.length - 1) {
          this.messageDelay = setTimeout(() => {
            this.currentMessageIndex.set(currentIndex + 1);
            this.typeNextMessage();
          }, 800);
        }
      }
    }, 30); // Typing speed
  }

  stopChatSimulation() {
    if (this.typewriterInterval) {
      clearInterval(this.typewriterInterval);
    }
    if (this.messageDelay) {
      clearTimeout(this.messageDelay);
    }
  }

  ngOnDestroy() {
    this.stopChatSimulation();
  }

  updateCreatorAudience(event: Event) {
    const value = (event.target as HTMLTextAreaElement).value;
    const currentModel = this.createBriefService.creatorProfileModel();
    if (currentModel) {
      this.createBriefService.creatorProfileModel.set({
        ...currentModel,
        creator_audience: value
      });
    }
  }

  updateCreatorContentStyle(event: Event) {
    const value = (event.target as HTMLTextAreaElement).value;
    const currentModel = this.createBriefService.creatorProfileModel();
    if (currentModel) {
      this.createBriefService.creatorProfileModel.set({
        ...currentModel,
        creator_content_style: value
      });
    }
  }

  updateCreatorEngagementMetrics(event: Event) {
    const value = (event.target as HTMLTextAreaElement).value;
    const currentModel = this.createBriefService.creatorProfileModel();
    if (currentModel) {
      this.createBriefService.creatorProfileModel.set({
        ...currentModel,
        creator_engagement_metrics: value
      });
    }
  }

  updateCreatorPersona(event: Event) {
    const value = (event.target as HTMLTextAreaElement).value;
    const currentModel = this.createBriefService.creatorProfileModel();
    if (currentModel) {
      this.createBriefService.creatorProfileModel.set({
        ...currentModel,
        creator_persona: value
      });
    }
  }

  updateCreatorSpecificCharacteristics(event: Event) {
    const value = (event.target as HTMLTextAreaElement).value;
    const currentModel = this.createBriefService.creatorProfileModel();
    if (currentModel) {
      this.createBriefService.creatorProfileModel.set({
        ...currentModel,
        creator_specific_characteristics: value
      });
    }
  }
}
